import {
  Card,
  CardContent,
  cn,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@devhop/ui";
import { TrendingUp } from "lucide-react";
import { AppImage } from "@/shared/ui/AppImage";
import type { BranchProductWithDetails } from "../../domain/types";
import { ProductImagePlaceholder } from "./ProductImagePlaceholder";

type ProductCardProps = {
  product: BranchProductWithDetails;
  onClick: () => void;
  onHover?: () => void;
};

export function ProductCard({ product, onClick, onHover }: ProductCardProps) {
  const isPopular = product.popularityScore > 0;

  return (
    <Card
      className={cn(
        "cursor-pointer select-none transition-all duration-150",
        "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
        "active:translate-y-0 active:scale-[0.98]",
        "flex flex-col gap-0 overflow-hidden py-0!",
      )}
      onClick={onClick}
      onPointerEnter={onHover}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-muted">
        <AppImage
          src={product.imageUrl}
          alt={product.name}
          className="aspect-square w-full"
          fit="cover"
          fallback={<ProductImagePlaceholder name={product.name} />}
        />
        {isPopular && (
          <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-primary px-2 py-1 font-medium text-primary-foreground text-xs shadow-sm">
            <TrendingUp className="h-3 w-3" />
            ຂายດີ
          </div>
        )}
        {product.variantCount > 1 && (
          <div className="absolute top-2 right-2 rounded-full bg-background/90 px-2 py-1 font-medium text-foreground text-xs shadow-sm backdrop-blur">
            {product.variantCount} รูปแบบ
          </div>
        )}
      </div>
      <CardContent className="flex flex-1 flex-col p-3">
        <h3 className="mb-1 line-clamp-2 font-medium text-sm leading-snug">
          {product.name}
        </h3>
        {product.description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="mb-2 line-clamp-1 text-muted-foreground text-xs">
                  {product.description}
                </p>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{product.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {product.minPrice !== null && (
          <p className="mt-auto pt-1 font-bold font-heading text-base text-primary">
            {product.variantCount > 1 && (
              <span className="mr-1 font-normal text-muted-foreground text-xs">
                ເລີ່ມ
              </span>
            )}
            {product.minPrice.toLocaleString("lo-LA")} ₭
          </p>
        )}
      </CardContent>
    </Card>
  );
}
